import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import useForceUpdate from 'hooks/useForceUpdate'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { usePlinkoCompletedBetListener } from 'views/InhouseGame/Plinko/hooks'
import { PlinkoBet } from 'views/InhouseGame/Plinko/types/Bet'
import InhouseGameBetDetailsModal from 'views/Profile/Transactions/components/Bet/InhouseGameBetDetailsModal'
import { InhouseGameDetails } from 'config/types/game'
import { Risk } from '../../config'
import { getPayouts } from '../PayoutGroup'

const UserPayoutUpdater = ({ subscribe }) => {
  usePlinkoCompletedBetListener(subscribe)

  return null
}

const UserPayout: React.FC<
  {
    row: number
    risk: Risk
    game: InhouseGameDetails
  } & BoxProps
> = ({ game, row, risk, ...props }) => {
  const betPayoutRef = useRef<PlinkoBet[]>([])

  const [onPresent] = useModal(InhouseGameBetDetailsModal)
  const { update: forceUpdate } = useForceUpdate()

  const payouts = useMemo(() => getPayouts(row, risk), [row, risk])
  const subscribe = useCallback(
    (bet) => {
      betPayoutRef.current = betPayoutRef.current.slice(0, 5)
      betPayoutRef.current.unshift(bet)
      forceUpdate()

      const timeout = setTimeout(() => {
        betPayoutRef.current = []
        forceUpdate()
      }, 30000)

      return () => {
        clearTimeout(timeout)
      }
    },
    [forceUpdate],
  )

  useEffect(() => {
    return () => {
      betPayoutRef.current = []
      forceUpdate()
    }
  }, [row])

  return (
    <StyledMultiplierGroup
      style={{
        fontSize: row < 14 ? '12px' : '10px',
      }}
      {...props}
    >
      <UserPayoutUpdater subscribe={subscribe} />
      {betPayoutRef.current.map((item: PlinkoBet) => {
        const payout = payouts?.[item.payoutIndex]
        if (!payout) return null
        return (
          <StyledBox
            key={`user-payout-${item.id}-${item.payoutIndex}`}
            onClick={() => {
              onPresent({
                betTxn: {
                  bet: {
                    amount: item.betAmount.amount.toString(10),
                    token: item.betAmount.token,
                    amountInUsd: item.betAmount.amount.toString(10),
                  },
                  game,
                  multiplier: payout.multiplier,
                  payout: {
                    amount: item.betAmount.amount.multipliedBy(payout.multiplier).toString(10),
                    token: item.betAmount.token,
                    amountInUsd: item.betAmount.amount.multipliedBy(payout.multiplier).toString(10),
                  },
                  providerBetIdRef: Number(item.id),
                },
              })
            }}
            background={`rgb(${payout.color.r}, ${payout.color.g}, ${payout.color.b})`}
          >
            <Flex
              height="100%"
              style={{
                fontSize: '0.75em',
              }}
              alignItems="center"
              justifyContent="center"
            >
              {payout.multiplier > 100 ? payout.multiplier : `${payout.multiplier}x`}{' '}
            </Flex>
          </StyledBox>
        )
      })}
    </StyledMultiplierGroup>
  )
}
const StyledBox = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;
  color: #000c;
  font-weight: 700;
  transition-property: background-color;
  transition-duration: 0.2s;
  cursor: pointer;
  font-size: 1em;

  width: 3em;
  height: 3em;
`

const StyledMultiplierGroup = styled(Column)`
  border-radius: 0.7em;
  overflow: hidden;

  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(-20%, -60%);

  ${StyledBox}:not(:first-child) {
    min-height: 3em;
  }

  ${StyledBox}:first-child {
    animation: 0.5s open;
  }

  @keyframes open {
    0% {
      max-height: 0em;
    }

    100% {
      max-height: 3em;
    }
  }

  ${({ theme }) => theme.mediaQueries.xl} {
    transform: translate(0, -60%);
  }

  width: 3em;
  height: 12em;
`

export default UserPayout
